// Utility functions for ModelDataSampleList component

import { Constants } from '../../../Models/Constants';
import { getPreferredUnits, getFlowTypeUnits } from '../../../Services/Global';
import { ColumnConfig } from './ModelDataSampleListTypes';

// Re-export the shared cell formatter so existing imports from this module
// keep working. The implementation now lives in Utils/CellUnitFormatter so it
// can be reused across all unit-aware tables (BlindTestList, etc.).
export {
    formatCellValue,
    getColumnUnitType,
    isDateColumnKey,
} from '../../../Utils/CellUnitFormatter';
export type { CellFormatContext, FormatCellValueOptions } from '../../../Utils/CellUnitFormatter';

/**
 * Media properties constants
 */
export const mediaProperties = [
    Constants.ColdMediaTempIn,
    Constants.HotMediaFlowrate,
    Constants.HotMediaTempIn,
    Constants.HotMediaTempOut,
    Constants.HotMediaPressureDrop,
    Constants.ColdMediaFlowrate,
    Constants.ColdMediaTempOut,
    Constants.ColdMediaPressureDrop
];

/**
 * Check if the column is a media property 
 */
export const isMediaProperty = (column: string): boolean => {
    return mediaProperties.some(property => property.toLowerCase() === column.toLowerCase());
};

/**
 * Update the session data for the data samples 
 */
export const updateSessionData = (sessionData: any[], columns: ColumnConfig[]): any[] => {
    return sessionData.map((data: any) => {
        let updatedData = { ...data };
        columns.forEach((column: any) => {
            if (isMediaProperty(column.id) && (updatedData[column.id] === 0 || updatedData[column.id] === null || updatedData[column.id] === undefined || updatedData[column.id] === '')) {
                updatedData[column.id] = "0";
            }
        });
        return updatedData;
    });
};

/**
 * Get preferred units for display
 */
export const getPreferredDisplayUnits = (flowType?: string) => {
    const preferredTemperatureUnits: string = `°${getPreferredUnits().preferredUnits === 'us' ? 'F' : 'C'}`;
    const preferredFlowrateUnits: string = flowType ? getFlowTypeUnits(flowType) : '';
    const preferredPressureUnits: string = getPreferredUnits().preferredUnits === 'us' ? 'PSI' : 'kPa';
    
    return {
        temperature: preferredTemperatureUnits,
        flowrate: preferredFlowrateUnits,
        pressure: preferredPressureUnits
    };
};

/**
 * Data filtering function for client-side operations
 */
export const getFilteredData = (data: any[], filterColumn: string, filterValue: string) => {
    if (!filterValue || !filterColumn || !data) return data;
    
    return data.filter(row => {
        const cellValue = (row as any)[filterColumn];
        if (cellValue == null) return false;
        
        return cellValue.toString().toLowerCase().includes(filterValue.toLowerCase());
    });
};

/**
 * Data sorting function
 */
export const sortData = (data: any[], columnId: string, sortDirection: 'asc' | 'desc') => {
    if (!columnId) return data;
    
    return [...data].sort((a, b) => {
        const aValue = (a as any)[columnId];
        const bValue = (b as any)[columnId];
        
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
        if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();
        
        return sortDirection === 'asc' 
            ? aString.localeCompare(bString)
            : bString.localeCompare(aString);
    });
};

/**
 * Apply pagination to sorted and filtered data
 */
export const applyPagination = (data: any[], currentPage: number, rowsPerPage: number) => {
    if (rowsPerPage === -1) {
        return data;
    }
    
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    return data.slice(startIndex, endIndex);
};

/**
 * Get sorted, filtered, and paginated data
 */
export const processTableData = (
    data: any[],
    filterColumn: string,
    filterValue: string,
    orderBy: string,
    order: 'asc' | 'desc',
    currentPage: number,
    rowsPerPage: number
) => {
    const filteredData = getFilteredData(data, filterColumn, filterValue);
    const sortedData = sortData(filteredData, orderBy, order);
    const paginatedData = applyPagination(sortedData, currentPage, rowsPerPage);
    
    return {
        processedData: paginatedData,
        totalFilteredCount: filteredData.length
    };
};

/**
 * Get color for cell content based on media type
 */
export const getCellColor = (columnKey: string, theme: any) => {
    const lowerKey = columnKey.toLowerCase();
    
    if (lowerKey.includes('hot')) {
        return theme.palette.error.main;
    } else if (lowerKey.includes('cold')) {
        return theme.palette.primary.main;
    }
    
    return theme.palette.text.primary;
}; 